import * as ExcelJS from "exceljs";
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import { NC_CLOSURE_METRICS_DATA } from "../../models/data.model";
import {
  QUALITE_C,
  downloadXlsx,
  qualiteHeaderStyle,
  qualiteRowStyle,
  qualiteSectionTitle,
  qualiteStatLabelStyle,
  qualiteStatStyle,
  qualiteSubHeaderStyle,
  renderChartToPng,
  todayIso,
} from "../qualite-excel-shared.utils";

Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
);

const DECISION_LABEL: Record<string, string> = {
  ACCEPTATION_DEROGATION: "Acceptation / dérogation",
  RETOUCHE: "Retraitement",
  REBUT: "Rebut",
  RETOUR_FOURNISSEUR: "Retour fournisseur",
  NON_RENSEIGNEE: "Non renseignée",
};

function buildTimelineChartPng(rows: NC_CLOSURE_METRICS_DATA["timeline"]): string | null {
  if (!rows?.length) return null;
  return renderChartToPng(
    (ctx) =>
      new Chart(ctx, {
        type: "bar",
        data: {
          labels: rows.map((r) => r.month),
          datasets: [
            {
              label: "Clôturées",
              data: rows.map((r) => r.cloturees),
              backgroundColor: "#36A2EB",
              stack: "total",
            },
            {
              label: "Ouvertes",
              data: rows.map((r) => r.ouvertes),
              backgroundColor: "#FF6384",
              stack: "total",
            },
          ],
        },
        options: {
          responsive: false,
          animation: false,
          plugins: {
            legend: { display: true, position: "bottom", labels: { font: { size: 11 } } },
            title: {
              display: true,
              text: "Évolution mensuelle — clôturées vs ouvertes",
              font: { size: 13, weight: "bold" },
            },
          },
          scales: {
            x: { stacked: true, ticks: { font: { size: 10 } } },
            y: {
              stacked: true,
              beginAtZero: true,
              ticks: { precision: 0, font: { size: 10 } },
            },
          },
        },
      }),
    700,
    340,
  );
}

function writeCoverSheet(wb: ExcelJS.Workbook, data: NC_CLOSURE_METRICS_DATA): void {
  const ws = wb.addWorksheet("Synthèse", {
    views: [{ state: "frozen", ySplit: 1 }],
    properties: { tabColor: { argb: QUALITE_C.headerBg } },
  });
  [22, 18, 18, 18, 18, 18].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:F1");
  qualiteSectionTitle(ws.getCell("A1"), "Taux de clôture des NC — Indicateurs IFS / ISO 9001");
  ws.getRow(1).height = 32;

  ws.mergeCells("A2:F2");
  const sub = ws.getCell("A2");
  sub.value = `Document généré le ${new Date().toLocaleDateString("fr-FR")} par JustUp`;
  sub.font = { size: 10, italic: true, color: { argb: "FF667085" } };

  const cards: { label: string; value: string | number; bg: string }[] = [
    { label: "Total NC créées", value: data.total, bg: QUALITE_C.statBlue },
    { label: "Clôturées", value: data.cloturees, bg: QUALITE_C.statGreen },
    { label: "Ouvertes", value: data.ouvertes, bg: QUALITE_C.statPurple },
    {
      label: "Taux de clôture (%)",
      value: data.tauxCloture == null ? "—" : data.tauxCloture,
      bg: QUALITE_C.statGreen,
    },
    {
      label: "Délai moyen (jours)",
      value: data.delaiMoyenJours == null ? "—" : data.delaiMoyenJours,
      bg: QUALITE_C.statOrange,
    },
    {
      label: "SLA J+30 (%)",
      value: data.slaJ30 == null ? "—" : data.slaJ30,
      bg: QUALITE_C.statRed,
    },
  ];
  cards.forEach((c, i) => {
    const labelCell = ws.getCell(4, i + 1);
    labelCell.value = c.label;
    qualiteStatLabelStyle(labelCell, c.bg);
    const valCell = ws.getCell(5, i + 1);
    valCell.value = c.value;
    qualiteStatStyle(valCell, c.bg);
    if (typeof c.value === "number") valCell.numFmt = "#,##0.##";
  });
  ws.getRow(4).height = 18;
  ws.getRow(5).height = 28;

  // Note de transparence sur le calcul
  ws.mergeCells("A8:F8");
  qualiteSectionTitle(ws.getCell("A8"), "Méthodologie");
  ws.getRow(8).height = 22;

  const notes = [
    `Périmètre : NC créées dans la fenêtre, hors annulées.`,
    `Taux de clôture = clôturées / (ouvertes + clôturées).`,
    `Délai moyen calculé sur ${data.delaiSamples} NC clôturées avec dateCloture renseignée.`,
    `SLA J+30 : % de NC clôturées en moins de 30 jours sur le total créé (les ouvertes pénalisent le SLA).`,
  ];
  let r = 10;
  for (const note of notes) {
    ws.mergeCells(r, 1, r, 6);
    const c = ws.getCell(r, 1);
    c.value = `• ${note}`;
    qualiteRowStyle(c, r % 2 === 0);
    c.alignment = { vertical: "middle", indent: 1 };
    r += 1;
  }
}

function writeDecisionSheet(
  wb: ExcelJS.Workbook,
  rows: NC_CLOSURE_METRICS_DATA["delaiParDecision"],
): void {
  const ws = wb.addWorksheet("Délai par décision", {
    views: [{ state: "frozen", ySplit: 5 }],
  });
  [32, 16, 18].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:C1");
  qualiteSectionTitle(ws.getCell("A1"), "Délai moyen de clôture par décision");
  ws.getRow(1).height = 28;

  const headerRow = 5;
  ["Décision", "Nb clôturées", "Délai moyen (j)"].forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  ws.autoFilter = { from: { row: headerRow, column: 1 }, to: { row: headerRow, column: 3 } };

  let r = headerRow + 1;
  for (const row of rows) {
    const a = ws.getCell(r, 1);
    a.value = DECISION_LABEL[row.decision] ?? row.decision;
    qualiteRowStyle(a, r % 2 === 0);
    a.font = { ...a.font, bold: true };
    const b = ws.getCell(r, 2);
    b.value = row.count;
    qualiteRowStyle(b, r % 2 === 0);
    b.alignment = { horizontal: "right" };
    b.numFmt = "#,##0";
    const c = ws.getCell(r, 3);
    c.value = row.delaiMoyenJours == null ? "" : row.delaiMoyenJours;
    qualiteRowStyle(c, r % 2 === 0);
    c.alignment = { horizontal: "right" };
    if (typeof row.delaiMoyenJours === "number") c.numFmt = "#,##0.0";
    r += 1;
  }

  if (rows.length > 0) {
    const tr = r + 1;
    const a = ws.getCell(tr, 1);
    a.value = "TOTAL";
    qualiteSubHeaderStyle(a);
    a.alignment = { horizontal: "right" };
    const b = ws.getCell(tr, 2);
    b.value = { formula: `SUM(B${headerRow + 1}:B${r - 1})` } as any;
    qualiteSubHeaderStyle(b);
    b.alignment = { horizontal: "right" };
    b.numFmt = "#,##0";
  }
}

function writeTimelineSheet(
  wb: ExcelJS.Workbook,
  rows: NC_CLOSURE_METRICS_DATA["timeline"],
): void {
  const ws = wb.addWorksheet("Évolution mensuelle", {
    views: [{ state: "frozen", ySplit: 5 }],
  });
  [16, 14, 14, 18].forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells("A1:D1");
  qualiteSectionTitle(ws.getCell("A1"), "Évolution mensuelle — Ouvertes vs Clôturées");
  ws.getRow(1).height = 28;

  const headerRow = 5;
  ["Mois", "Ouvertes", "Clôturées", "Délai moyen (j)"].forEach((h, i) => {
    const c = ws.getCell(headerRow, i + 1);
    c.value = h;
    qualiteHeaderStyle(c);
  });
  ws.getRow(headerRow).height = 22;
  ws.autoFilter = { from: { row: headerRow, column: 1 }, to: { row: headerRow, column: 4 } };

  let r = headerRow + 1;
  for (const m of rows) {
    const cells: (string | number | null)[] = [
      m.month,
      m.ouvertes,
      m.cloturees,
      m.delaiMoyenJours == null ? null : m.delaiMoyenJours,
    ];
    cells.forEach((v, i) => {
      const c = ws.getCell(r, i + 1);
      c.value = v as any;
      qualiteRowStyle(c, r % 2 === 0);
      if (i === 0) c.font = { ...c.font, bold: true };
      if (i > 0) {
        c.alignment = { horizontal: "right" };
        c.numFmt = i === 3 ? "#,##0.0" : "#,##0";
      }
    });
    r += 1;
  }

  if (rows.length > 0) {
    const tr = r + 1;
    const a = ws.getCell(tr, 1);
    a.value = "TOTAL";
    qualiteSubHeaderStyle(a);
    a.alignment = { horizontal: "right" };
    const b = ws.getCell(tr, 2);
    b.value = { formula: `SUM(B${headerRow + 1}:B${r - 1})` } as any;
    qualiteSubHeaderStyle(b);
    b.alignment = { horizontal: "right" };
    b.numFmt = "#,##0";
    const c = ws.getCell(tr, 3);
    c.value = { formula: `SUM(C${headerRow + 1}:C${r - 1})` } as any;
    qualiteSubHeaderStyle(c);
    c.alignment = { horizontal: "right" };
    c.numFmt = "#,##0";
  }

  // Chart
  const png = buildTimelineChartPng(rows);
  if (png) {
    const imgId = wb.addImage({ base64: png, extension: "png" });
    ws.addImage(imgId, {
      tl: { col: 5, row: 4 } as any,
      ext: { width: 700, height: 340 },
    });
  }
}

export async function exportNcClosureMetricsExcel(
  data: NC_CLOSURE_METRICS_DATA,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, {
    title: "Taux de clôture NC",
    subject: "Indicateurs ISO 9001 / IFS — Délais de clôture",
    description:
      "Suivi du taux de clôture des non-conformités, délai moyen, SLA J+30, " +
      "ventilation par décision (acceptation, retouche, rebut, retour fournisseur), " +
      "évolution mensuelle. Document généré par JustUp.",
    keywords: "JustUp, IFS, ISO 9001, NC, clôture, SLA, délai",
  });

  writeCoverSheet(wb, data);
  if (data.delaiParDecision?.length) writeDecisionSheet(wb, data.delaiParDecision);
  if (data.timeline?.length) writeTimelineSheet(wb, data.timeline);

  for (const ws of wb.worksheets) {
    const lastCol = Math.max(6, ws.actualColumnCount || 6);
    await addJustupBranding(wb, ws, lastCol);
  }

  const buffer = (await wb.xlsx.writeBuffer()) as ArrayBuffer;
  downloadXlsx(buffer, `nc-closure-metrics-${todayIso()}.xlsx`);
}
